/**
 * @module lib/rpdm/refreshBus
 * @summary Explicit emit/subscribe contract for W2 cache invalidation.
 * @description Pure-JS module singleton implementing the emit table
 * frozen in Rev.2 § 15.2. No React coupling; hooks that need a
 * component-scoped subscription live in `src/hooks/useRpdmRefreshBus.js`.
 *
 * ## Purpose
 * - Give W2 write wrappers a deterministic way to invalidate the reads
 *   held by other surfaces without introducing a server-state library.
 * - Preserve UI-I-01 · Deterministic Navigation by keeping URL as the
 *   single source of truth for route state — the bus never mutates URLs.
 *
 * ## Inputs / Outputs
 * - `emit(keys: string[])` — fan-out notification. Returns nothing.
 * - `subscribe(keys: string[], handler: () => void)` — returns an
 *   `unsubscribe` closure MUST be invoked in effect cleanup (§ 11.0-C).
 * - `_reset()` — test-only helper; clears the entire registry.
 *
 * ## Invariants (Phase 1a)
 * - Side-effect free at import time. Registry is a lazily-initialised
 *   private `const` map. No timers, no globals, no listeners.
 * - No existing surface may subscribe during Phase 1a. Phase 1b wiring
 *   begins the first subscribers.
 * - Fan-out order is stable and matches insertion order.
 * - `_reset` must be called explicitly by tests; never runs implicitly.
 *
 * ## Future consuming phase
 * - Phase 2: `useHierarchyTree` subscribes to `engagement:{id}` +
 *   `wi:{id}` after mount.
 * - Phase 3 → 5: additional surfaces per the Rev.2 § 15.2 emit table.
 */

/**
 * Module-scope handler registry. Keys are string bus keys (`wi:{id}`,
 * `engagement:{id}`, `effort:{id}` …); values are `Set<() => void>`.
 * Held in a private module-scope binding so consumers cannot mutate it.
 * @type {Map<string, Set<() => void>>}
 */
const registry = new Map();

/**
 * Normalise / validate a bus key.
 * @param {string} key
 * @returns {string}
 * @throws {Error} in non-production builds when the key is malformed.
 */
function assertKey(key) {
    if (typeof key !== "string" || key.length === 0) {
        throw new Error("rpdm:refreshBus · key must be a non-empty string");
    }
    // Rev.2 § 15.2 · normalise: lower-case, no whitespace.
    if (/\s/.test(key) || key !== key.toLowerCase()) {
        if (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production") {
            throw new Error(
                "rpdm:refreshBus · key must be lowercase and contain no whitespace: " + JSON.stringify(key),
            );
        }
    }
    return key;
}

/**
 * Broadcast a set of keys to all subscribers. Fan-out is synchronous and
 * in insertion order.
 *
 * @param {ReadonlyArray<string>} keys - one or more bus keys to invalidate.
 * @returns {void}
 * @invariant Never mutates URL / history state.
 * @invariant Handlers run synchronously; exceptions are caught and reported
 *            to `console.error` (in non-production only) so one bad handler
 *            cannot cancel fan-out for the rest.
 */
export function emit(keys) {
    if (!Array.isArray(keys) || keys.length === 0) return;
    for (let i = 0; i < keys.length; i += 1) {
        const key = assertKey(keys[i]);
        const set = registry.get(key);
        if (!set || set.size === 0) continue;
        for (const handler of set) {
            try {
                handler();
            } catch (err) {
                if (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production") {
                    console.error("rpdm:refreshBus · handler threw for key " + key, err);
                }
            }
        }
    }
}

/**
 * Register a handler for one or more bus keys.
 *
 * @param {ReadonlyArray<string>} keys - one or more bus keys to watch.
 * @param {() => void} handler - invoked whenever any of `keys` is emitted.
 * @returns {() => void} unsubscribe closure — call this in your effect
 *   cleanup to detach the handler (§ 11.0-C mandatory).
 * @invariant Returns a fresh closure each call. Repeated `subscribe` calls
 *            for the same key + handler pair are permitted and each must
 *            be independently unsubscribed.
 */
export function subscribe(keys, handler) {
    if (!Array.isArray(keys) || keys.length === 0 || typeof handler !== "function") {
        return () => undefined;
    }
    const validated = new Array(keys.length);
    for (let i = 0; i < keys.length; i += 1) {
        validated[i] = assertKey(keys[i]);
    }
    for (let i = 0; i < validated.length; i += 1) {
        let set = registry.get(validated[i]);
        if (!set) {
            set = new Set();
            registry.set(validated[i], set);
        }
        set.add(handler);
    }
    let disposed = false;
    return function unsubscribe() {
        if (disposed) return;
        disposed = true;
        for (let i = 0; i < validated.length; i += 1) {
            const set = registry.get(validated[i]);
            if (!set) continue;
            set.delete(handler);
            if (set.size === 0) registry.delete(validated[i]);
        }
    };
}

/**
 * Test-only: wipe the registry. Never call from production code.
 * @returns {void}
 * @invariant Only intended for unit-test cleanup in Phase 1c.
 */
export function _reset() {
    registry.clear();
}

/**
 * Test-only inspector.
 * @returns {number} number of unique keys currently subscribed.
 */
export function _size() {
    return registry.size;
}
